import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Card, Modal } from "react-bootstrap";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEye, faPen, faTrash, faFilter, faSync, faCopyright, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import PaginatedItems from '../../../../components/administracao/Pagination';
import RequireRoles from '../../../../components/RequireRoles';
import useFetch from '../../../../hooks/useFetch';
import Swal from 'sweetalert2';

export default function GerenciarDireitos() {
    const { params } = useParams();
    const { get, del } = useFetch();

    const defaultState = {
        direitos: [],
        count: 0,
        filtros: { nome_direito: "" },
        filtroAtivo: false,
        loading: true,
        mostrarModal: false,
        registroAExcluir: {}
    };

    const [state, setState] = useState(() => defaultState);

    useEffect(() => {
        async function initialize() {
            let [direitos, count] = await buscarDireitos({});
            setState(s => ({ ...s, direitos, count, loading: false }));
        }
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    async function buscarDireitos(filtros) {
        let query = "?";
        if (filtros && Object.keys(filtros).length > 0) {
            for (const key in filtros) {
                if (filtros[key] !== "") query = query.concat(key, "=", filtros[key], "&");
            }
        }
        try {
            let response = await get('/direitoautoral' + query);
            if (response.ok) {
                let dados = response.result;
                return [dados.data, dados.count];
            }
            return [[], 0];
        } catch (error) {
            console.error('Erro no buscarDireitos():', error);
            return [[], 0];
        }
    }

    async function handlePageChange(pageNumber) {
        window.scrollTo(0, 0);
        let filtros = state.filtroAtivo ? { ...state.filtros, page: pageNumber } : { page: pageNumber };
        let [direitos, count] = await buscarDireitos(filtros);
        setState({ ...state, direitos, count, loading: false });
    }

    const filtrarLocal = async () => {
        let [direitos, count] = await buscarDireitos(state.filtros);
        setState(s => ({ ...s, direitos, count, filtroAtivo: true, loading: false }));
    };

    const limparFiltros = async () => {
        let [direitos, count] = await buscarDireitos({});
        setState(s => ({ ...s, filtros: defaultState.filtros, direitos, count, filtroAtivo: false, loading: false }));
    };

    async function excluirDireito() {
        try {
            let response = await del('/direitoautoral/' + state.registroAExcluir.id_direito);
            if (response.status === 200) {
                let [direitos, count] = await buscarDireitos(state.filtroAtivo ? state.filtros : {});
                setState({ ...state, direitos, count, mostrarModal: false, registroAExcluir: {}, loading: false });
            } else if (response.status === 409) {
                setState({ ...state, mostrarModal: false });
                Swal.fire({ title: "Não foi possível excluir", text: response.result?.message ?? "Direito em uso por alguma fotografia.", icon: "error", confirmButtonColor: "#587b7f" });
            } else {
                console.error('Erro no excluirDireito()');
                Swal.fire({ title: "Não foi possível excluir o direito", text: "Erro inesperado, tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
            }
        } catch (error) {
            console.error('Erro no excluirDireito():', error);
            Swal.fire({ title: "Erro de conexão", text: "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    return (
        <div className="admin-page-container">
            <div className="admin-header-container">
                <div className="title-group">
                    <h2 className="admin-title">Direitos Autorais</h2>
                    <p className="admin-subtitle">Gerencie as permissões de uso e reprodução das imagens</p>
                </div>
                <Link to="/admin/iconografico/direitos/criar" className="btn-add-new">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Novo Direito
                </Link>
            </div>

            <Card className="filter-card-admin mb-4">
                <Card.Body className="p-4">
                    <Form className="row g-3 align-items-end" onSubmit={(e) => { e.preventDefault(); filtrarLocal(); }}>
                        <Form.Group className="col-md-8">
                            <Form.Label className="label-admin">Nome do Direito / Licença</Form.Label>
                            <Form.Control
                                className="input-moderno"
                                placeholder="Ex: Domínio Público, Creative Commons..."
                                value={state.filtros.nome_direito}
                                onChange={e => setState({ ...state, filtros: { nome_direito: e.target.value } })}
                            />
                        </Form.Group>
                        <div className="col-md-4 d-flex gap-2">
                            <Button type="button" className="btn-apply-filters w-100" onClick={filtrarLocal}>
                                <FontAwesomeIcon icon={faFilter} className="me-2" /> Filtrar
                            </Button>
                            <Button type="button" className="btn-clear-filters" onClick={limparFiltros}>
                                <FontAwesomeIcon icon={faSync} />
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="table-card-admin">
                <div className="table-responsive">
                    <Table className="modern-table mb-0">
                        <thead>
                            <tr>
                                <th>Nome do Direito</th>
                                <th>Descrição</th>
                                <th className="text-center">Permite Reprodução?</th>
                                <th className="text-center">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr><td colSpan="4" className="text-center py-5"><div className="spinner-border text-teal"></div></td></tr>
                            ) : state.direitos.length > 0 ? (
                                state.direitos.map((item) => (
                                    <tr key={item.id_direito}>
                                        <td data-label="Nome do Direito" className="fw-bold">
                                            <FontAwesomeIcon icon={faCopyright} className="me-2 text-muted" />
                                            {item.nome_direito}
                                        </td>
                                        <td data-label="Descrição" className="text-muted small text-truncate-custom" title={item.descricao}>
                                            {item.descricao}
                                        </td>
                                        <td data-label="Permite Reprodução?" className="text-center">
                                            {item.permite_reproducao ?
                                                <span className="badge bg-success-light text-success border-success-subtle px-3 rounded-pill">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Sim
                                                </span> :
                                                <span className="badge bg-danger-light text-danger border-danger-subtle px-3 rounded-pill">
                                                    <FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Não
                                                </span>
                                            }
                                        </td>
                                        <td data-label="Opções">
                                            <div className="d-flex justify-content-center gap-2">
                                                <Link className="action-icon view" to={`/admin/iconografico/direitos/${item.id_direito}`} state={{ acao: "Visualizar" }}>
                                                    <FontAwesomeIcon icon={faEye} />
                                                </Link>
                                                <Link className="action-icon edit" to={`/admin/iconografico/direitos/${item.id_direito}`} state={{ acao: "Editar" }}>
                                                    <FontAwesomeIcon icon={faPen} />
                                                </Link>
                                                <RequireRoles allowedRoles={['Desenvolvedor', 'Administrador']}>
                                                    <button className="action-icon delete" onClick={() => setState({ ...state, mostrarModal: true, registroAExcluir: item })}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </RequireRoles>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="4" className="text-center py-4 text-muted">Nenhum direito cadastrado.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                {state.direitos.length > 0 && (
                    <div className="card-footer bg-white border-0 py-3 d-flex justify-content-center">
                        <PaginatedItems itemCount={state.count} itemsPerPage={20} handlePageChange={handlePageChange} />
                    </div>
                )}
            </Card>

            <Modal show={state.mostrarModal} onHide={() => setState({ ...state, mostrarModal: false })} centered className="modern-modal">
                <Modal.Header closeButton className="modal-header-teal">
                    <Modal.Title className="text-white fs-5">Excluir Regra de Direito</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-0">Deseja excluir a regra <strong>{state.registroAExcluir.nome_direito}</strong>?</p>
                </Modal.Body>
                <Modal.Footer className="border-0 p-4 pt-0">
                    <Button variant="light" onClick={() => setState({ ...state, mostrarModal: false })}>Cancelar</Button>
                    <Button variant="danger" className="px-4" onClick={excluirDireito}>Excluir</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
